import { reactive, watch } from 'vue'
import { v1 as uuid } from 'uuid'
import { cloneDeep } from 'lodash-es'


type MCInternalMessageType = 'message' | 'result'
type MCErrorCode = 'unexpected' | 'timeout'

interface MCBasicInternalMessage {
  internalType: MCInternalMessageType
  transactionId: string
  payload: any
}

interface MCInternalRequestMessage<MessageType> extends MCBasicInternalMessage {
  internalType: 'message'
  messageType: MessageType
  needResponse: boolean
  fromContextId: string
  toContextId: string | null
}

interface MCInternalResultMessage extends MCBasicInternalMessage {
  internalType: 'result'
  result: boolean
  errorCode?: MCErrorCode
}

type MCInternalMessage<MessageType> =
  MCInternalRequestMessage<MessageType>
  | MCInternalResultMessage


// TODO: must depend on response payload type
type MCRequestResult = {
  result: boolean
  errorCode?: MCErrorCode
  payload: any
}

type MCListenerCallback<T = any> = (payload: T) => any
type MCListenerRecord = {
  callback: MCListenerCallback
  once: boolean
  listenerUid: string
}

type ConnectedContextsRecord = {
  contextId: string
  timestamp: number
}

const DEFAULT_OPERATION_TIMEOUT = 10_000
const ABANDONED_RESULTS_TTL = 10 * 60 * 1000
const TREAT_SENDER_INACTIVE = 60 * 1000


export class MessageChannel<InMessageType, OutMessageType> {

  private channel: BroadcastChannel | null = null
  private contextId: string
  private connectedContexts: ConnectedContextsRecord[] = []
  private listeners = new Map<InMessageType, MCListenerRecord[]>()
  private waitingRequests = reactive<Record<string, MCRequestResult>>({})
  private madeRequests = reactive<Record<string, { timestamp: number }>>({})


  constructor(channelName: string, contextId: string) {

    if (!contextId) {
      console.error('NO contextId passed to MessageChannel')
    }

    this.channel = new BroadcastChannel(channelName)
    this.contextId = contextId
    this.channel.addEventListener('message', this.onMessage.bind(this))

    // garbage collecting:
    setInterval(() => {
      const now = Date.now()
      Object.keys(this.madeRequests).forEach((transactionId) => {
        if (now - this.madeRequests[transactionId].timestamp > ABANDONED_RESULTS_TTL) {
          if (this.waitingRequests[transactionId]) {
            delete this.waitingRequests[transactionId]
          }
          delete this.madeRequests[transactionId]
        }
      })
    }, ABANDONED_RESULTS_TTL)
  }

  private onMessage(event: MessageEvent<MCInternalMessage<InMessageType>>) {

    const message = event.data
    // console.log(`'⇄ onMessage (${
    //   message.internalType === 'message'
    //     ? message.internalType + ', ' + message.messageType
    //     : 'result'
    // })`)

    const testSendCallbackResult = (wasError: boolean, responsePayload: any) => {
      if (
        'needResponse' in message
        && message.needResponse
      ) {
        this.channel?.postMessage({
          internalType: 'result',
          transactionId: message.transactionId,
          result: !wasError,
          errorCode: wasError ? 'unexpected' : undefined,
          payload: cloneDeep(responsePayload),
        } satisfies MCInternalResultMessage)
      }
    }

    if (message.internalType === 'result') {
      // if !madeRequests -> not for current sender / very outdated response
      if (this.madeRequests[message.transactionId]) {
        // it may occur, that result came after timeout, or we got > 1 answer -> GC made by timer
        this.waitingRequests[message.transactionId] = {
          result: message.result,
          errorCode: message.errorCode,
          payload: message.payload,
        }
      }
      return
    }

    this.registerSenderContextId(message.fromContextId)

    if (message.toContextId && message.toContextId !== this.contextId) {
      return
    }

    const messageListeners = this.listeners.get(message.messageType) ?? []

    messageListeners.forEach((listenerRecord) => {
      try {
        const responsePayload = listenerRecord.callback(message.payload)
        if (responsePayload instanceof Promise) {
          responsePayload
            .then((payload) => testSendCallbackResult(false, payload))
            .catch(() => testSendCallbackResult(true, null))
        } else {
          testSendCallbackResult(false, responsePayload)
        }
      } catch {
        testSendCallbackResult(true, null)
      }

      if (listenerRecord.once) {
        this.removeListener(message.messageType, listenerRecord.listenerUid)
      }
    })
  }

  private registerSenderContextId(contextId: string) {
    if (!contextId) {
      return
    }

    const now = Date.now()
    this.connectedContexts = this.connectedContexts
      .filter((record) => now - record.timestamp < TREAT_SENDER_INACTIVE)

    const record = this.connectedContexts.find((record) => record.contextId === contextId)
    if (record) {
      record.timestamp = Date.now()
    } else {
      this.connectedContexts.push({ contextId, timestamp: Date.now() })
    }
  }

  getSenderContextIdsSorted() {
    return [...this.connectedContexts]
      .sort((a, b) => b.timestamp - a.timestamp) // newest first
      .map((record) => record.contextId)
  }

  addListener(
    type: InMessageType,
    callback: MCListenerCallback,
    once: boolean = false,
  ) {
    const messageListeners = this.listeners.get(type) ?? []

    const listenerUid = uuid()
    messageListeners.push({
      callback,
      listenerUid,
      once,
    })
    this.listeners.set(type, messageListeners)

    return listenerUid
  }

  removeListener(type: InMessageType, listenerUid: string) {
    let messageListeners = this.listeners.get(type)
    if (messageListeners) {
      messageListeners = messageListeners.filter((listener) => {
        return listener.listenerUid !== listenerUid
      })

      if (messageListeners.length) {
        this.listeners.set(type, messageListeners)
      } else {
        this.listeners.delete(type)
      }
    }
  }

  sendMessage(
    type: OutMessageType,
    payload: any = null,
    options: { toContextId?: string } = { toContextId: undefined },
  ): void {
    const transactionId = uuid()
    // console.log(`⇄ sendMessage (${type})`)

    this.channel?.postMessage({
      internalType: 'message',
      messageType: type,
      needResponse: false,
      fromContextId: this.contextId,
      toContextId: options.toContextId ?? null,
      transactionId,
      payload,
    } satisfies MCInternalRequestMessage<OutMessageType>)
  }

  sendMessageWithResult(
    type: OutMessageType,
    payload: any = null,
    options: {
      timeout?: number
      toContextId?: string
    } = {
      timeout: undefined,
      toContextId: undefined,
    },
  ): Promise<MCRequestResult> {
    const transactionId = uuid()
    // console.log(`⇄ sendMessageWithResult (${type})`)

    return new Promise((resolve) => {

      const unWatch = watch(this.waitingRequests, () => {
        const requestResult = this.waitingRequests[transactionId]
        if (requestResult) {
          unWatch()
          delete this.waitingRequests[transactionId]
          resolve(cloneDeep(requestResult))
        }
      })

      const signal = AbortSignal.timeout(options.timeout ?? DEFAULT_OPERATION_TIMEOUT)

      signal.addEventListener('abort', () => {
        unWatch()
        resolve({
          result: false,
          errorCode: 'timeout',
          payload: null,
        })
      }, { once: true })

      this.channel?.postMessage({
        internalType: 'message',
        messageType: type,
        needResponse: true,
        fromContextId: this.contextId,
        toContextId: options.toContextId ?? null,
        transactionId,
        payload,
      } satisfies MCInternalRequestMessage<OutMessageType>)

      this.madeRequests[transactionId] = { timestamp: Date.now() }
    })
  }

  close() {
    this.channel?.close()
    this.channel = null
  }
}
